import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  BrainCircuit,
  Loader2,
  Truck,
  Zap,
  Hand,
  ShieldQuestion,
  Package,
  ChevronUp,
  ChevronDown,
  Pencil,
  Trash2,
  Plus,
  ArrowRight,
  ShieldCheck,
  IndianRupee,
  Undo2,
  Lightbulb,
  AlertTriangle,
  Info,
  Sparkles,
  Globe,
  Home,
} from 'lucide-react';
import { ErrorState } from '@/components/common/ErrorState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  dispatchService,
  dispatchRulesService,
  type CarrierScorecard,
  type DispatchAssignmentRule,
  type DispatchPartner,
  type RoutingRecommendation,
} from '@/services/dispatch';
import { deliveryTiersService } from '@/services/deliveryTiers';
import { RuleFormSheet, DispatchRulesPage } from './DispatchRulesPage';
import { findShadowedRules } from './ruleIntel';
import { TierCard, HowItWorks } from './DeliveryTiersPage';
import { DispatchPage } from './DispatchPage';
import { DispatchPartnersPage } from './DispatchPartnersPage';
import { DeliveryQuotesPage } from './DeliveryQuotesPage';
import { DispatchLabelsPage } from '../dispatch-labels/DispatchLabelsPage';
import { fmt } from '@/lib/format';

const METHOD_LABEL: Record<string, string> = {
  TIER_AUTO: 'Auto-optimized',
  FIXED_PARTNER: 'Fixed partner',
  RULE_BASED: 'Rule-based',
  OPERATOR: 'Operator pick',
  CARRIER_AUTO: 'Carrier auto-routed',
  UNRESOLVED: 'Not yet routed',
};

const METHOD_ICON: Record<string, typeof Truck> = {
  TIER_AUTO: Zap,
  FIXED_PARTNER: Truck,
  RULE_BASED: BrainCircuit,
  OPERATOR: Hand,
  CARRIER_AUTO: ShieldQuestion,
  UNRESOLVED: Package,
};

const METHOD_ORDER = [
  'TIER_AUTO',
  'FIXED_PARTNER',
  'RULE_BASED',
  'OPERATOR',
  'CARRIER_AUTO',
  'UNRESOLVED',
];

function methodLabel(m: string | null): string {
  return METHOD_LABEL[m ?? 'UNRESOLVED'] ?? m ?? '—';
}

function Spinner() {
  return (
    <div className="flex h-48 items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

/**
 * The Dispatcher — the delivery control plane. One place to CONFIGURE
 * (tiers, carriers, routing rules) and to WATCH (how each shipment got its
 * carrier + carrier performance). Tabbed so it stays legible as it grows.
 */
export function DispatcherPage() {
  const insightsQuery = useQuery({
    queryKey: ['dispatch', 'dispatcher-insights'],
    queryFn: () => dispatchService.routingInsights(),
  });
  const tiersQuery = useQuery({
    queryKey: ['delivery-tiers'],
    queryFn: () => deliveryTiersService.list(),
  });
  const rulesQuery = useQuery({
    queryKey: ['dispatch', 'rules'],
    queryFn: () => dispatchRulesService.list(),
  });
  const partnersQuery = useQuery({
    queryKey: ['dispatch', 'partners'],
    queryFn: () => dispatchService.listPartners(),
    staleTime: 60_000,
  });

  const activePartners = (partnersQuery.data ?? []).filter((p) => p.status === 'ACTIVE');
  const tiers = (tiersQuery.data ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder);
  const ruleBasedTiers = tiers.filter((t) => t.fulfilmentMode === 'RULE_BASED');
  const data = insightsQuery.data;

  return (
    <div className="space-y-4">
      <Tabs defaultValue="setup" className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-border/60 pb-2">
          <div className="flex items-baseline gap-2">
            <h1 className="text-[15px] font-semibold tracking-tight text-foreground">Dispatcher</h1>
            <span className="hidden text-[11.5px] text-muted-foreground sm:inline">
              control plane — partners, tiers, quotes, dispatch &amp; analytics
            </span>
          </div>
          <TabsList>
            <TabsTrigger value="setup">Tiers</TabsTrigger>
            <TabsTrigger value="partners">Partners</TabsTrigger>
            <TabsTrigger value="rules">Routing rules</TabsTrigger>
            <TabsTrigger value="quotes">Quotes &amp; audit</TabsTrigger>
            <TabsTrigger value="dispatch">Dispatch</TabsTrigger>
            <TabsTrigger value="labels">Shipping Labels</TabsTrigger>
            <TabsTrigger value="performance">Analytics</TabsTrigger>
          </TabsList>
        </div>

        {/* ── Partners — configure the carriers the routing can assign ── */}
        <TabsContent value="partners" className="space-y-4">
          <DispatchPartnersPage embedded />
        </TabsContent>

        {/* ── Tiers & options — the configuration ───────────────────── */}
        <TabsContent value="setup" className="space-y-5">
          <HowItWorks />
          {tiersQuery.isLoading ? (
            <Spinner />
          ) : tiersQuery.error ? (
            <ErrorState error={tiersQuery.error} onRetry={() => tiersQuery.refetch()} />
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {tiers.map((t) => (
                <TierCard key={t.tier} tier={t} partners={activePartners} />
              ))}
            </div>
          )}

          {ruleBasedTiers.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-[13px] font-semibold text-foreground">
                Routing rules · for rule-based tiers
              </h3>
              {ruleBasedTiers.map((t) => (
                <TierRulesPanel
                  key={t.tier}
                  tierCode={t.tier}
                  rules={rulesQuery.data ?? []}
                  partners={activePartners}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Performance — analysis space (carrier scorecards + insights) ── */}
        <TabsContent value="performance" className="space-y-5">
          {insightsQuery.error ? (
            <ErrorState error={insightsQuery.error} onRetry={() => insightsQuery.refetch()} />
          ) : !data ? (
            <Spinner />
          ) : (
            <>
              <div className="flex items-center justify-between gap-3">
                <p className="text-[12px] text-muted-foreground">
                  {data.config.partners.length} carrier
                  {data.config.partners.length === 1 ? '' : 's'} connected ·{' '}
                  {data.config.ruleCount} routing rule{data.config.ruleCount === 1 ? '' : 's'}.
                </p>
                <Link
                  to="/dispatch/partners"
                  className="inline-flex items-center gap-1 text-[12px] font-medium text-primary hover:underline"
                >
                  Manage carriers <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              <CarrierInsights performance={data.performance} />

              <RoutingRecommendations recommendations={data.recommendations ?? []} />

              <section className="space-y-3">
                <h3 className="flex items-center gap-2 text-[13px] font-semibold text-foreground">
                  Carrier performance
                  <span className="text-[11.5px] font-normal text-muted-foreground">
                    · from your own delivery outcomes · will feed reliability-weighted routing
                  </span>
                </h3>
                {data.performance.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-border px-4 py-6 text-center text-[12.5px] text-muted-foreground">
                    Building up — scorecards appear as shipments are delivered (or returned). Every
                    outcome is recorded per carrier to learn who actually performs.
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-2xl border border-border">
                    <table className="w-full text-left text-[12.5px]">
                      <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2 font-medium">Carrier / courier</th>
                          <th className="px-3 py-2 text-right font-medium">Shipments</th>
                          <th className="px-3 py-2 text-right font-medium">On-time</th>
                          <th className="px-3 py-2 text-right font-medium">Avg transit</th>
                          <th className="px-3 py-2 text-right font-medium">RTO</th>
                          <th className="px-3 py-2 text-right font-medium">Avg cost</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/60">
                        {data.performance.map((s, i) => (
                          <tr key={`${s.partnerCode}-${s.courierName}-${i}`} className="hover:bg-muted/20">
                            <td className="px-3 py-2.5">
                              <div className="text-foreground">{s.courierName ?? s.partnerCode}</div>
                              {s.courierName && (
                                <div className="font-mono text-[10px] text-muted-foreground">
                                  {s.partnerCode}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums">{s.shipments}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums">
                              {s.onTimePct != null ? (
                                <span
                                  className={
                                    s.onTimePct >= 90
                                      ? 'text-success'
                                      : s.onTimePct < 75
                                        ? 'text-destructive'
                                        : 'text-foreground'
                                  }
                                >
                                  {s.onTimePct}%
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">
                              {s.avgTransitDays != null ? `${s.avgTransitDays}d` : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums">
                              <span className={s.rtoPct > 5 ? 'text-destructive' : 'text-muted-foreground'}>
                                {s.rtoPct}%
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">
                              {s.avgRate != null ? fmt.money(s.avgRate, 'INR') : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section className="space-y-3">
                <h3 className="flex items-center gap-2 text-[13px] font-semibold text-foreground">
                  <BrainCircuit className="h-4 w-4 text-primary" />
                  How shipments were routed
                  <span className="text-[11.5px] font-normal text-muted-foreground">
                    · last {data.windowDays} days · {data.summary.total} dispatch
                    {data.summary.total === 1 ? '' : 'es'}
                  </span>
                </h3>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                  {METHOD_ORDER.filter((m) => (data.summary.byMethod[m] ?? 0) > 0).map((m) => {
                    const Icon = METHOD_ICON[m] ?? Package;
                    const count = data.summary.byMethod[m] ?? 0;
                    const pct =
                      data.summary.total > 0 ? Math.round((count / data.summary.total) * 100) : 0;
                    return (
                      <div key={m} className="rounded-2xl border border-border bg-card p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-[11.5px] text-muted-foreground">
                          <Icon className="h-3.5 w-3.5" />
                          {methodLabel(m)}
                        </div>
                        <div className="mt-1 flex items-baseline gap-2">
                          <span className="text-[22px] font-bold tabular-nums text-foreground">
                            {count}
                          </span>
                          <span className="text-[12px] text-muted-foreground">{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
                  {data.summary.total === 0 && (
                    <div className="col-span-full rounded-2xl border border-dashed border-border px-4 py-6 text-center text-[12.5px] text-muted-foreground">
                      No dispatches in the last {data.windowDays} days yet. Routing decisions appear
                      here as orders ship.
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </TabsContent>

        {/* ── Routing rules — region/warehouse → partner assignment ──── */}
        <TabsContent value="rules" className="space-y-4">
          <DispatchRulesPage embedded />
        </TabsContent>

        {/* ── Quotes & audit — trace every EDD + charge end-to-end ──── */}
        <TabsContent value="quotes" className="space-y-4">
          <DeliveryQuotesPage />
        </TabsContent>

        {/* ── Dispatch — the full operational list (searchable) ─────── */}
        <TabsContent value="dispatch" className="space-y-4">
          <DispatchPage embedded />
        </TabsContent>

        {/* ── Labels — AWB lookup + print queue ─────────────────────── */}
        <TabsContent value="labels" className="space-y-4">
          <DispatchLabelsPage embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InsightCard({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: typeof Truck;
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-1 truncate text-[15px] font-semibold text-foreground">{value}</div>
      <div className="text-[11px] text-muted-foreground">{sub}</div>
    </div>
  );
}

/**
 * Analysis headline derived from the carrier scorecards — the start of the
 * optimization surface. As outcomes accumulate this becomes the basis for
 * reliability-weighted routing recommendations.
 */
const REC_STYLE = {
  WARN: { icon: AlertTriangle, ring: 'ring-destructive/30', bg: 'bg-destructive/[0.06]', fg: 'text-destructive' },
  SUGGEST: { icon: Lightbulb, ring: 'ring-primary/30', bg: 'bg-primary/[0.06]', fg: 'text-primary' },
  INFO: { icon: Info, ring: 'ring-border', bg: 'bg-muted/30', fg: 'text-muted-foreground' },
} as const;

/**
 * Routing recommendations — actionable guidance the system derives from the
 * tenant's own carrier outcomes, recomputed continuously. The bridge from
 * "here's the performance data" to "here's what to do for auto-tier routing".
 */
function RoutingRecommendations({ recommendations }: { recommendations: RoutingRecommendation[] }) {
  if (recommendations.length === 0) return null;
  return (
    <section className="space-y-3">
      <h3 className="flex items-center gap-2 text-[13px] font-semibold text-foreground">
        <Sparkles className="h-4 w-4 text-primary" />
        Routing recommendations
        <span className="text-[11.5px] font-normal text-muted-foreground">
          · generated from your delivery outcomes · sharpen as dispatches complete
        </span>
      </h3>
      <div className="grid gap-2.5 lg:grid-cols-2">
        {recommendations.map((r) => {
          const s = REC_STYLE[r.severity] ?? REC_STYLE.INFO;
          const Icon = s.icon;
          return (
            <div
              key={r.id}
              className={`flex items-start gap-2.5 rounded-xl px-3.5 py-3 ring-1 ring-inset ${s.ring} ${s.bg}`}
            >
              <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${s.fg}`} />
              <div className="min-w-0">
                <div className="text-[12.5px] font-semibold text-foreground">{r.title}</div>
                <p className="mt-0.5 text-[11.5px] leading-relaxed text-muted-foreground">{r.detail}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CarrierInsights({ performance }: { performance: CarrierScorecard[] }) {
  const ranked = performance.filter((p) => p.shipments > 0);
  const name = (s: CarrierScorecard) => s.courierName ?? s.partnerCode;

  if (ranked.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-primary/30 bg-primary/5 px-4 py-4 text-[12.5px] text-muted-foreground">
        <span className="font-medium text-foreground">Analysis builds as you ship.</span> Once
        delivery outcomes accumulate, this space surfaces who delivers most reliably, with the
        fewest returns and the lowest cost — and will power automatic reliability-weighted routing
        (the next optimization step).
      </div>
    );
  }

  const bestOnTime = [...ranked]
    .filter((s) => s.onTimePct != null)
    .sort((a, b) => (b.onTimePct ?? 0) - (a.onTimePct ?? 0))[0];
  const lowestRto = [...ranked].sort((a, b) => a.rtoPct - b.rtoPct)[0];
  const cheapest = [...ranked]
    .filter((s) => s.avgRate != null)
    .sort((a, b) => (a.avgRate ?? 0) - (b.avgRate ?? 0))[0];

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <InsightCard
        icon={ShieldCheck}
        label="Most reliable"
        value={bestOnTime ? name(bestOnTime) : '—'}
        sub={bestOnTime?.onTimePct != null ? `${bestOnTime.onTimePct}% on-time` : 'no SLA data yet'}
      />
      <InsightCard
        icon={Undo2}
        label="Fewest returns"
        value={lowestRto ? name(lowestRto) : '—'}
        sub={lowestRto ? `${lowestRto.rtoPct}% RTO` : ''}
      />
      <InsightCard
        icon={IndianRupee}
        label="Lowest cost"
        value={cheapest ? name(cheapest) : '—'}
        sub={cheapest?.avgRate != null ? `${fmt.money(cheapest.avgRate, 'INR')} avg` : 'no cost data yet'}
      />
    </div>
  );
}

function TierRulesPanel({
  tierCode,
  rules,
  partners,
}: {
  tierCode: 'STANDARD' | 'EXPRESS';
  rules: DispatchAssignmentRule[];
  partners: DispatchPartner[];
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<DispatchAssignmentRule | null>(null);
  const [adding, setAdding] = useState(false);

  const partnerLabel = (id: string) => {
    const p = partners.find((x) => x.id === id);
    return p?.name ?? p?.code ?? '—';
  };

  // Rules that govern this tier: tier-scoped to it + any-tier fallbacks.
  const applicable = useMemo(
    () =>
      rules
        .filter((r) => r.tier === tierCode || r.tier == null)
        .sort((a, b) => a.priority - b.priority),
    [rules, tierCode],
  );

  // Routing preview — simulate which partner this tier routes to for a
  // domestic vs an international shipment, from the ACTIVE rules in priority
  // order. A no-condition ALL rule is a catch-all that covers both.
  const { domesticRule, intlRule } = useMemo(() => {
    const active = applicable.filter((r) => r.status === 'ACTIVE');
    const isCatchAll = (r: DispatchAssignmentRule) =>
      r.scope === 'ALL' &&
      r.countryIn.length === 0 &&
      r.stateIn.length === 0 &&
      r.warehouseCodeIn.length === 0;
    return {
      domesticRule: active.find((r) => r.scope === 'DOMESTIC' || isCatchAll(r)) ?? null,
      intlRule: active.find((r) => r.scope === 'INTERNATIONAL' || isCatchAll(r)) ?? null,
    };
  }, [applicable]);

  // Misconfiguration check — applicable rules made unreachable by a broader
  // rule above them (e.g. an "All destinations" rule shadowing International).
  const shadowed = useMemo(() => findShadowedRules(applicable), [applicable]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['dispatch', 'rules'] });
  const reorder = useMutation({
    mutationFn: ({ id, priority }: { id: string; priority: number }) =>
      dispatchRulesService.update(id, { priority }),
    onSuccess: invalidate,
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Could not reorder'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => dispatchRulesService.remove(id),
    onSuccess: () => {
      toast.success('Rule deleted');
      invalidate();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Could not delete'),
  });

  return (
    <div className="rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-3.5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Rules for {tierCode === 'STANDARD' ? 'Standard' : 'Express'} · lower number wins
        </span>
        <Button size="sm" variant="outline" className="h-7" onClick={() => setAdding(true)}>
          <Plus className="h-3.5 w-3.5" /> Add rule
        </Button>
      </div>

      {/* Routing preview — what this tier ACTUALLY does, by destination. */}
      <div className="mb-2.5 grid gap-2 sm:grid-cols-2">
        {([
          { key: 'dom', Icon: Home, label: 'Domestic', rule: domesticRule },
          { key: 'intl', Icon: Globe, label: 'International', rule: intlRule },
        ] as const).map(({ key, Icon, label, rule }) => (
          <div
            key={key}
            className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-[12px] ${
              rule ? 'border-border/60 bg-card' : 'border-destructive/30 bg-destructive/[0.05]'
            }`}
          >
            <Icon className={`h-4 w-4 shrink-0 ${rule ? 'text-primary' : 'text-destructive'}`} />
            <span className="font-medium text-foreground">{label}</span>
            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            {rule ? (
              <span className="truncate font-semibold text-foreground">
                {partnerLabel(rule.partnerId)}
              </span>
            ) : (
              <span className="truncate text-[11px] text-destructive">
                not routed — shipments will stall
              </span>
            )}
          </div>
        ))}
      </div>

      {shadowed.size > 0 && (
        <div className="mb-2 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/[0.06] px-2.5 py-2 text-[11.5px]">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
          <span className="text-foreground">
            A broader rule shadows {shadowed.size === 1 ? 'a more specific one' : 'more specific ones'} —
            so the preview above may not be what you intend. Narrow the higher rule&rsquo;s destination
            (e.g. set it to <strong>Domestic</strong>), or reorder.
          </span>
        </div>
      )}

      {applicable.length === 0 ? (
        <p className="text-[11.5px] text-[hsl(38_85%_30%)]">
          No rules apply yet — add one, or this tier's shipments won't be routed.
        </p>
      ) : (
        <div className="space-y-1">
          {applicable.map((r, i) => {
            const isShadowed = shadowed.has(r.id);
            const scopeLabel =
              r.scope === 'DOMESTIC'
                ? 'Domestic'
                : r.scope === 'INTERNATIONAL'
                  ? 'International'
                  : null;
            const refine: string[] = [];
            if (r.countryIn.length) refine.push(r.countryIn.join(', '));
            if (r.stateIn.length) refine.push(r.stateIn.join(', '));
            if (r.warehouseCodeIn.length) refine.push(`WH ${r.warehouseCodeIn.join(', ')}`);
            const cond =
              [scopeLabel, ...refine].filter(Boolean).join(' · ') || 'Any destination';
            return (
              <div
                key={r.id}
                className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[12px] ${
                  isShadowed ? 'border-destructive/30 bg-destructive/[0.04]' : 'border-border/60 bg-card'
                }`}
              >
                <span className="w-7 shrink-0 font-mono text-[10px] text-muted-foreground">
                  #{r.priority}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate font-medium text-foreground">{r.name}</span>
                    <Badge variant={r.tier ? 'success' : 'muted'} className="text-[9px]">
                      {r.tier ? 'This tier' : 'Any'}
                    </Badge>
                    {isShadowed && (
                      <Badge variant="muted" className="gap-0.5 text-[9px] text-destructive">
                        <AlertTriangle className="h-2.5 w-2.5" /> Never fires
                      </Badge>
                    )}
                    {r.status !== 'ACTIVE' && (
                      <Badge variant="muted" className="text-[9px]">
                        Off
                      </Badge>
                    )}
                  </div>
                  <div className="truncate text-[10.5px] text-muted-foreground">
                    {cond} → {partnerLabel(r.partnerId)}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  <button
                    type="button"
                    disabled={i === 0 || reorder.isPending}
                    onClick={() => reorder.mutate({ id: r.id, priority: Math.max(0, r.priority - 1) })}
                    className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                    aria-label="Higher priority"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={i === applicable.length - 1 || reorder.isPending}
                    onClick={() => reorder.mutate({ id: r.id, priority: r.priority + 1 })}
                    className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                    aria-label="Lower priority"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(r)}
                    className="p-1 text-muted-foreground hover:text-foreground"
                    aria-label="Edit"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Delete rule "${r.name}"?`)) remove.mutate(r.id);
                    }}
                    className="p-1 text-muted-foreground hover:text-destructive"
                    aria-label="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <RuleFormSheet open={adding} onOpenChange={setAdding} partners={partners} defaultTier={tierCode} />
      <RuleFormSheet
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        partners={partners}
        rule={editing}
      />
    </div>
  );
}

